import type { SpliceProvider } from "@sigilry/dapp";
import { useCallback, useEffect, useRef, useState } from "react";
import { CantonBrowserConnector } from "../../core/canton/cantonConnector";
import {
  AnnouncedCantonProvider,
  discoverCantonProviders,
} from "../../core/canton/cip103Client";
import {
  DiscoveredInjectedCantonWallet,
  cantonClientFromSpliceProvider,
  discoverInjectedCantonWallets,
} from "../../core/canton/injectedCantonProvider";
import { CantonWallet, ConnectorID } from "../../core/types/wallet";
import {
  formatCantonWallet,
  mergeInjectedCantonWallets,
} from "../../services/internal/cantonService";

/**
 * Discovers Canton browser-extension wallets and maps each to a
 * {@link CantonWallet}: CIP-103 announce-protocol wallets (on mount and on
 * window focus), plus injected wallets that ship the CIP-103 RPC surface as a
 * `window` global instead of announcing (see `INJECTED_CANTON_WALLETS`).
 */
export function useCantonWallets(): { wallets: CantonWallet[] } {
  const [wallets, setWallets] = useState<CantonWallet[]>([]);
  const announcedCache = useRef(new Map<string, CantonBrowserConnector>());
  const injectedCache = useRef(
    new Map<string, { connector: CantonBrowserConnector; provider: SpliceProvider }>()
  );

  const toAnnouncedWallet = useCallback(
    (provider: AnnouncedCantonProvider): CantonWallet => {
      let connector = announcedCache.current.get(provider.id);
      if (!connector) {
        connector = new CantonBrowserConnector(provider);
        announcedCache.current.set(provider.id, connector);
      }
      return formatCantonWallet({
        connectorId: provider.id as ConnectorID,
        name: provider.name,
        icon: provider.icon,
        connector,
      });
    },
    []
  );

  const toInjectedWallet = useCallback(
    ({ config, provider }: DiscoveredInjectedCantonWallet): CantonWallet => {
      // Rebuild if the wallet re-injects a new provider object (e.g. extension
      // reload): the cached connector closes over the previous provider.
      let entry = injectedCache.current.get(config.connectorId);
      if (!entry || entry.provider !== provider) {
        entry = {
          connector: new CantonBrowserConnector(
            { id: config.connectorId, name: config.name, icon: config.icon },
            window,
            cantonClientFromSpliceProvider(provider)
          ),
          provider,
        };
        injectedCache.current.set(config.connectorId, entry);
      }
      return formatCantonWallet({
        connectorId: config.connectorId,
        name: config.name,
        icon: config.icon,
        connector: entry.connector,
      });
    },
    []
  );

  const refresh = useCallback(async () => {
    if (typeof window === "undefined") return;
    const announced = (await discoverCantonProviders()).map(toAnnouncedWallet);
    const injected = discoverInjectedCantonWallets().map(toInjectedWallet);
    setWallets(mergeInjectedCantonWallets(announced, injected));
  }, [toAnnouncedWallet, toInjectedWallet]);

  useEffect(() => {
    refresh();
    if (typeof window === "undefined") return undefined;
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    // Extensions may inject their global shortly after mount; re-check a couple
    // of times so late injection surfaces without a focus change.
    const retries = [500, 1500].map((ms) => window.setTimeout(refresh, ms));
    return () => {
      window.removeEventListener("focus", onFocus);
      retries.forEach((id) => window.clearTimeout(id));
    };
  }, [refresh]);

  return { wallets };
}
